"use client";

import { getRecentConsoleErrors } from "@/components/error-reporter";

/** 聊天页的错误边界。
 *
 *  在此之前这页没有任何边界：客户端一抛异常，Next 就把整页换成一句
 *  「Application error: a client-side exception has occurred」——用户看到白屏，
 *  我们也拿不到任何线索，只能靠用户去翻控制台（真机 2026-08-15，
 *  一个普通用户账号登录后必崩，来回问了好几轮都定位不到）。
 *
 *  这里做三件事：
 *  1. 页面不再整个死掉，还能重试或回首页；
 *  2. **把真实报错显示出来**，用户截个图就能定位，不用教他开 F12；
 *  3. 把报错上报到服务端日志（失败也不影响展示）。
 */

import { useEffect } from "react";

export default function ChatError({
  error,
  reset
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // 上报失败不能再抛，否则错误边界自己又崩了
    try {
      void fetch("/api/client-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // React 的组件栈只在 console.error 里，#185 靠它定位
          consoleErrors: getRecentConsoleErrors(),
          message: error?.message ?? "",
          stack: (error?.stack ?? "").slice(0, 4000),
          digest: error?.digest ?? "",
          url: typeof location !== "undefined" ? location.href : "",
          ua: typeof navigator !== "undefined" ? navigator.userAgent : ""
        })
      }).catch(() => {});
    } catch {
      /* 上报是尽力而为 */
    }
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="w-full max-w-lg space-y-4 text-center">
        <h2 className="font-serif text-2xl font-medium">页面出了点问题</h2>
        <p className="text-sm text-muted-foreground">
          可以先点「重试」。如果反复出现，把下面这段发给我们能更快定位。
        </p>

        <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-xl border border-border bg-secondary/40 p-3 text-left text-xs text-muted-foreground">
          {error?.message || "未知错误"}
          {error?.digest ? `\n\ndigest: ${error.digest}` : ""}
        </pre>

        <div className="flex justify-center gap-3">
          <button
            type="button"
            onClick={reset}
            className="h-10 rounded-full bg-foreground px-5 text-sm text-background transition-opacity hover:opacity-90"
          >
            重试
          </button>
          <button
            type="button"
            onClick={() => {
              // 崩溃常常源于本地存的旧格式数据，给个不用开 F12 的出口。
              // 只清本地：服务端历史不受影响，重新登录后都还在。
              try {
                localStorage.clear();
              } catch {
                /* 隐私模式下会抛 */
              }
              location.href = "/chat";
            }}
            className="h-10 rounded-full border border-border px-5 text-sm transition-colors hover:bg-secondary"
          >
            清除本地数据并重进
          </button>
        </div>
      </div>
    </div>
  );
}
